import { type RequestConfig, history } from "@umijs/max";
import { message } from "antd"; // 假设你使用 Ant Design 的 message 组件

/**
 * 运行时请求配置（Request Config）
 * 包含了错误统一处理和请求拦截
 */
export const errorConfig: RequestConfig = {
  // ===================================
  // 请求前置配置 (Interceptor Request)
  // ===================================
  requestInterceptors: [
    (config: any) => {
      // 统一注入 Token、时间戳等
      const token = localStorage.getItem("ACCESS_TOKEN");
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
  ],

  // ===================================
  // 响应后置配置 (Interceptor Response)
  // ===================================
  responseInterceptors: [
    (response) => {
      // 假设后端返回的数据结构是 { success: boolean, code: number, data: any, message: string }
      const { data } = response as any;

      // 文件下载返回 Blob，不包含业务 code；交给下载方处理文件及 JSON 错误内容。
      // HTTP 4xx/5xx 仍由请求库拒绝，不能因为下载模式而吞掉真正的失败。
      if (response.config?.responseType === "blob" && data instanceof Blob) {
        return response;
      }

      // 普通 JSON 接口仍按业务 code 判断是否成功。
      if (Number(data?.code) !== 200 && response.url !== "/api/login") {
        const error: any = new Error(data?.message || "请求失败，请稍后再试");
        error.name = "BizError";
        error.response = response;
        throw error;
      }

      return response;
    },
  ],

  // ===================================
  // 全局错误处理配置 (Error Config)
  // ===================================
  // 重点：这是一个用于统一处理请求错误的配置对象
  errorConfig: {
    // 错误统一处理，通常用于提示用户
    // error 是一个 Error 对象，可能是网络错误、超时、4xx/5xx HTTP 错误，或业务错误（如上面手动抛出的 BizError）
    errorHandler: (error: any, opts: any) => {
      const { data, status } = error.response || {};

      // 1. 会话恢复独立于业务错误提示；公开认证请求和登录页不能被其他 401 中断。
      if (status === 401) {
        if (!opts?.skipAuthRedirect && window.location.pathname !== "/user/login") {
          const token = localStorage.getItem("ACCESS_TOKEN");
          const headers = error.config?.headers || error.response?.config?.headers;
          const authorization = headers?.get?.("Authorization")
            ?? headers?.Authorization ?? headers?.authorization ?? "";
          // 只处理当前已登录会话的失败，旧 token 或未登录请求的迟到响应不能撤销新会话。
          if (token && authorization === `Bearer ${token}`) {
            localStorage.removeItem("ACCESS_TOKEN");
            // 页面接管错误提示时不再重复弹出全局提示，但仍恢复到登录页。
            if (!opts?.skipErrorHandler) {
              message.error(data?.message || "登录已失效，请重新登录。");
            }
            history.replace("/user/login");
          }
        }
        throw error;
      }

      // 2. 表单校验、业务错误及网络错误按原约定交由页面展示。
      if (opts?.skipErrorHandler) {
        throw error;
      }

      // 其他 HTTP 状态码错误 (4xx, 5xx)
      if (status) {
        switch (status) {
          case 200:
            if (Number(data?.code) !== 200) {
              message.error(data?.message || "请求失败");
            }
            break;
          case 403: // Forbidden (禁止访问)
            message.error("您没有访问权限！");
            break;
          case 404: // Not Found (资源不存在)
            message.error("请求的资源不存在。");
            break;
          case 500: // Internal Server Error (服务器内部错误)
            message.error("服务器发生错误，请联系管理员。");
            break;
          default:
            message.error(`请求错误：状态码 ${status}`);
        }
      }
      // 自定义业务错误 (BizError) 或其他网络错误
      else if (error.name === "BizError") {
        // 处理在 responseInterceptors 中抛出的业务逻辑错误
        message.error(error.message || "业务处理失败。");
      } else {
        // 网络或其他客户端错误 (如：CORS 错误、网络断开等)
        message.error("网络连接异常或请求超时，请检查您的网络。");
      }

      // 最终将错误抛出，以便 useRequest 或 Promise.catch() 继续捕获
      throw error;
    },
  },
};
